import {withSuspenseLoader} from '@yandex-delivery/blocks'
import {DrawerOverlay} from '@yandex-delivery/blocks/legacy'
import clsx from 'clsx'
import React, {lazy, useMemo} from 'react'
import type {SharedRouteInfoResponse} from 'src/api/types'
import {useTheme} from 'src/shared/hooks'

import {useIsMobile, useSharedRoutePerformer} from '../../hooks'
import {MapModes} from '../../types'
import {getMapProps} from '../../utils'
import {AccentButton} from '../accent-button'
import {OpenRoverButton} from '../action-open-rover'
import {BackButton} from '../back-button'
import {BottomContentSections, Content} from '../content'
import {DrawerDesktop, DrawerMobile, DrawerSection, DrawerSection as DrawerSectionDesktop} from '../drawer'
import {ExtendExpirationButton} from '../extend-expiration-action'
import {Feedback} from '../feedback'
import {Header} from '../header'
// biome-ignore lint/suspicious/noShadowRestrictedNames: компонент карты, глобальный Map в модуле не нужен
import {Map} from '../map'
import {Payment} from '../payment'
import {Poll} from '../poll'
import {PostcardDesktop, PostcardMobile} from '../postcard'
import {Status} from '../status'
import {Timeline} from '../timeline'
import {classes} from './ActiveScreen.classes'
import {toActionsPayload} from './ActiveScreen.utils'

const Actions = withSuspenseLoader(lazy(() => import('../actions')))
const WebTips = withSuspenseLoader(lazy(() => import('../web-tips')))

type Props = {
  info: SharedRouteInfoResponse
}

const ActiveScreenAuth: React.FC<Props> = ({info}) => {
  const theme = useTheme()
  const performer = useSharedRoutePerformer()
  const {
    mapProps,
    headActions,
    paymentAction,
    webTipsAction,
    extendExpirationAction,
    footActions,
    isShowFeedback,
    openRoverActions,
  } = useMemo(() => {
    return Object.assign(toActionsPayload(info), {
      mapProps: getMapProps(info, performer),
    })
  }, [info, performer])

  const contentSections = info.content_sections

  const isMobile = useIsMobile()

  const {DrawerComponent, DrawerSectionComponent, Postcard} = useMemo(() => {
    if (isMobile) {
      return {
        DrawerComponent: DrawerMobile,
        DrawerSectionComponent: DrawerSection,
        Postcard: PostcardMobile,
      }
    }

    return {
      DrawerComponent: DrawerDesktop,
      DrawerSectionComponent: DrawerSectionDesktop,
      Postcard: PostcardDesktop,
    }
  }, [isMobile])

  return (
    <DrawerOverlay zIndex={950}>
      <div dir="ltr">
        <Map {...mapProps} theme={theme} />
      </div>
      <Header variant={info.postcard ? 'postcard' : undefined} />
      <DrawerComponent>
        <BackButton />
        {!!info.poll && (
          <div className={classes.drawerSection}>
            <DrawerSectionComponent>
              <Poll poll={info.poll} />
            </DrawerSectionComponent>
          </div>
        )}
        <div className={clsx(classes.drawerSection, classes.content)}>
          <DrawerSectionComponent>
            <Status info={info} />
            {info.timeline && <Timeline showBubble timeline={info.timeline} />}
            {isMobile &&
              openRoverActions.length > 0 &&
              openRoverActions.map((item, index) => <OpenRoverButton action={item} key={`open-rover-btn-${index}`} />)}
            {isShowFeedback && mapProps.mode === MapModes.Delivered && <Feedback />}
            {webTipsAction && <WebTips webTipsAction={webTipsAction} />}
            {headActions.length > 0 && <Actions actions={headActions} />}
          </DrawerSectionComponent>
          {paymentAction && (
            <DrawerSectionComponent>
              <Payment paymentAction={paymentAction} />
            </DrawerSectionComponent>
          )}
          {info.accent_state_button && (
            <DrawerSectionComponent>
              <AccentButton button={info.accent_state_button} />
            </DrawerSectionComponent>
          )}

          {contentSections && <Content sections={contentSections} />}
          {extendExpirationAction && (
            <DrawerSectionComponent>
              <ExtendExpirationButton action={extendExpirationAction} />
            </DrawerSectionComponent>
          )}
          {footActions.length > 0 && (
            <DrawerSectionComponent fullHeight>
              <Actions actions={footActions} />
            </DrawerSectionComponent>
          )}
        </div>
        {info.bottom_content_sections && (
          <BottomContentSections floating={isMobile} sections={info.bottom_content_sections} />
        )}
      </DrawerComponent>

      {info.postcard && <Postcard data={info.postcard} />}
    </DrawerOverlay>
  )
}

export default ActiveScreenAuth
